'use client';

import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { useRadio } from '@react-aria/radio';
import { mergeRefs } from '@react-aria/utils';
import { type RadioGroupState } from '@react-stately/radio';
import { type ReactNode, useId, useRef } from 'react';

import { type VariantProps } from 'tailwind-variants';

import { FormControlLabel } from '../../internal/components/form/FormControlLabel';
import { filterProps } from '../../internal/utils/filterProps';
import { focusRingStyles } from '../../styles/focusRing';
import { type Component, type ComponentProps } from '../../types/component';
import { mergeProps } from '../../utils/mergeProps';
import { tv } from '../../utils/tv';
import { CoreIcon, type CoreIconProps } from '../core-icon/CoreIcon';
import { useRadioGroupContext } from '../radio-group/RadioGroupContext';

const resolveIconColor = (
  selected: boolean | undefined,
  color: RadioProps['color'],
  disabled: RadioProps['isDisabled'],
  isInvalid: boolean | undefined
): CoreIconProps['color'] => {
  if (disabled) {
    return 'disabled';
  }
  if (selected) {
    return color;
  }
  if (isInvalid) {
    return 'error';
  }
  return color === 'primary' ? 'secondary' : color;
};

const getAriaDescribedBy = (...ids: (string | undefined)[]) => {
  return ids.filter(Boolean).join(' ') || undefined;
};

type Variant = VariantProps<typeof radioStyles>;

interface RadioSlotProps {
  /** Additional props for the hidden `<input>` element. */
  input?: ComponentProps<object, 'input'>;
}

interface RadioBaseProps {
  /**
   * The value of the `Radio`, used when submitting an HTML form.
   */
  value: string;
  /**
   * Whether the `Radio` is disabled.
   */
  isDisabled?: boolean;
  /**
   * Whether the `Radio` should receive focus on render.
   */
  autoFocus?: boolean;

  /**
   * The `width` and `height` of the `Radio`.
   * @default 'md'
   */
  size?: Variant['size'];
  /**
   * The color of the `Radio`.
   * @default 'primary'
   */
  color?: Variant['color'];
  /**
   * The positioning of the `Radio` icon in relation to the label text.
   * @default 'left'
   */
  iconPosition?: Variant['iconPosition'];
  /**
   * The vertical alignment of the radio icon.
   * @default 'start'
   */
  alignIcon?: Variant['alignIcon'];
  /**
   * Provide the user with context or feedback for the input.
   * Shown below the `Radio`.
   *
   * Validation messaging (error/success) is handled at the `RadioGroup` level
   * rather than per `Radio`.
   */
  description?: string;
  /** The label content displayed for the `Radio`. When omitted, provide `aria-labelledby` for accessibility. */
  label?: ReactNode;
  /** Content rendered at the end of the label row (e.g., "$5.00/night"). Requires `label` to be set. */
  endLabel?: ReactNode;
  /** Content rendered between the radio icon and the label. */
  leftSlot?: ReactNode;
  /**
   * Props for internal sub-elements.
   * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-radio--overview#slotprops | slotProps documentation}
   */
  slotProps?: RadioSlotProps;
}

const radioClasses = {
  root: 'zds-radio-root',
  wrapperLabel: 'zds-radio-wrapperlabel',
  input: 'zds-radio-input',
  iconWrapper: 'zds-radio-iconwrapper',
  radioIcon: 'zds-radio-icon-radio',
  radioCheckedIcon: 'zds-radio-icon-radiochecked',
  label: 'zds-radio-label',
} as const;

const radioStyles = tv({
  slots: {
    root: [radioClasses.root, 'flex', 'flex-col'],
    wrapperLabel: [
      radioClasses.wrapperLabel,
      'flex',
      'cursor-pointer',
      'items-start',
      'z--radio-padding-bottom',
      'z--radio-padding-top',
      'z--radio-gap',
    ],
    input: [radioClasses.input, 'sr-only'],
    iconWrapper: [
      radioClasses.iconWrapper,
      'flex',
      'items-center',
      'justify-center',
      'shrink-0',
    ],
    icon: [], // Identifier classes differ per icon state; applied at render.
    label: [radioClasses.label],
  },
  variants: {
    iconPosition: {
      left: '',
      right: {
        wrapperLabel: 'justify-between',
        iconWrapper: 'order-1',
      },
    },
    alignIcon: {
      start: '',
      center: {
        iconWrapper: 'self-center',
      },
    },
    size: {
      sm: {
        iconWrapper: ['z--radio-sm-height', 'z--radio-sm-width'],
      },
      md: {
        iconWrapper: ['z--radio-md-height', 'z--radio-md-width'],
      },
    },
    color: {
      primary: {
        label: 'text-primary',
      },
      white: {
        label: 'text-static-white-main',
      },
    },
    isDisabled: {
      true: {
        wrapperLabel: 'cursor-not-allowed',
        label: 'disabled-foreground',
      },
    },
  },
});

type RadioProps = ComponentProps<RadioBaseProps, 'div'>;

type RadioComponent = Component<RadioBaseProps, 'div'>;

const Radio: RadioComponent = (props) => {
  const [localProps, radioRaProps, hoverRaProps, focusRingRaProps] =
    filterProps(props, ['useRadio', 'useHover', 'useFocusRing']);

  // Component-specific props + restLocalProps for the DOM element
  const {
    size: propsSize,
    iconPosition: propsIconPosition,
    alignIcon: propsAlignIcon,
    color: propsColor,
    className,
    description,
    label,
    endLabel,
    leftSlot,
    slotProps,
    ref,
    ...restLocalProps
  } = localProps;

  // Hook-claimed props also needed for rendering
  const { value } = props;

  const internalInputRef = useRef<HTMLInputElement>(null);

  const textId = useId();
  const labelTextId = useId();
  const endLabelId = useId();

  const { hoverProps, isHovered } = useHover(hoverRaProps);
  const { isFocusVisible, isFocused, focusProps } =
    useFocusRing(focusRingRaProps);

  const {
    state: groupState,
    color: groupColor,
    size: groupSize,
    iconPosition: groupIconPosition,
    alignIcon: groupAlignIcon,
  } = useRadioGroupContext();

  if (process.env['NODE_ENV'] !== 'production' && !groupState) {
    console.warn('Radio: must be used inside a RadioGroup.');
  }

  if (
    process.env['NODE_ENV'] !== 'production' &&
    groupState &&
    props.value == null
  ) {
    console.warn(
      'Radio: `value` prop is required when used inside a RadioGroup.'
    );
  }

  const { labelProps, inputProps, isSelected, isDisabled, isPressed } =
    useRadio(
      {
        ...radioRaProps,
        children: label,
        value: value ?? '',
      },
      // groupState is always set as Radio should always be rendered inside RadioGroup.
      groupState as RadioGroupState,
      internalInputRef
    );

  const color = propsColor || groupColor || 'primary';
  const size = propsSize || groupSize || 'md';
  const iconPosition = propsIconPosition || groupIconPosition || 'left';
  const alignIcon = propsAlignIcon || groupAlignIcon || 'start';
  const isInvalid = groupState?.isInvalid;

  const styles = radioStyles({
    color,
    size,
    iconPosition,
    alignIcon,
    isDisabled,
  });

  const focusStyles = focusRingStyles({
    color: color === 'white' ? 'static-white' : 'default',
  });

  const iconColor = resolveIconColor(isSelected, color, isDisabled, isInvalid);

  // Only scope the accessible name via aria-labelledby when label is set
  // and the consumer hasn't provided their own aria-label or aria-labelledby.
  const shouldScopeLabel =
    label && !props['aria-label'] && !props['aria-labelledby'];

  return (
    <div
      {...mergeProps(restLocalProps, hoverProps)}
      className={styles.root({ className: [focusStyles.group(), className] })}
      ref={ref}
      data-disabled={isDisabled || undefined}
      data-required={inputProps['aria-required'] || undefined}
      data-focused={isFocused || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-selected={isSelected || undefined}
      data-pressed={isPressed || undefined}
      data-hovered={isHovered || undefined}
      data-invalid={isInvalid || undefined}
    >
      <label {...labelProps} className={styles.wrapperLabel({ size })}>
        <input
          {...mergeProps(inputProps, focusProps, slotProps?.input ?? {})}
          className={styles.input({ className: slotProps?.input?.className })}
          ref={mergeRefs(internalInputRef, slotProps?.input?.ref)}
          {...(shouldScopeLabel ? { 'aria-labelledby': labelTextId } : {})}
          aria-describedby={getAriaDescribedBy(
            endLabel ? endLabelId : undefined,
            description ? textId : undefined,
            inputProps['aria-describedby']
          )}
        />

        <span className={styles.iconWrapper()}>
          <span
            className={focusStyles.item({
              className: `inline-flex rounded-full outline-offset-0 ${!isSelected ? '' : 'hidden'}`,
            })}
          >
            <CoreIcon
              color={iconColor}
              size={size}
              name="radio"
              className={styles.icon({
                className: radioClasses.radioIcon,
              })}
            />
          </span>
          <span
            className={focusStyles.item({
              className: `inline-flex rounded-full outline-offset-0 ${isSelected ? '' : 'hidden'}`,
            })}
          >
            <CoreIcon
              color={iconColor}
              size={size}
              name="radiochecked"
              className={styles.icon({
                className: radioClasses.radioCheckedIcon,
              })}
            />
          </span>
        </span>
        <FormControlLabel
          labelTextId={shouldScopeLabel ? labelTextId : undefined}
          endLabelId={endLabel ? endLabelId : undefined}
          label={label}
          isDisabled={isDisabled}
          leftSlot={leftSlot}
          endLabel={endLabel}
          color={color}
          className={styles.label()}
          descriptionId={textId}
          description={description}
          isInvalid={isInvalid}
          size={size}
          alignIcon={alignIcon}
        />
      </label>
    </div>
  );
};

Radio.displayName = 'Radio';

export { Radio, type RadioProps, type RadioBaseProps, radioClasses };
